// Competition team stats and leaderboard arithmetic.
//
// SIX stats here, and they are NOT the Rumaal Rank rubric — that one has four
// (Smile, Execution, Energy, Grace/Nakhra). Three names appear in both systems
// and mean different things in each: a ceremony's Execution is a judge's mark
// on tested material, a competition's Execution is a coach's read across a
// season. Never average one into the other, and never reuse utils/scoring.js
// here.
//
// Pure ESM with no Node or Mongoose dependencies, so the frontend and the
// parent app can re-export this module rather than reimplementing the maths.

export const COMPETITION_STATS = [
  { key: 'ability', label: 'Ability', short: 'ABIL' },
  { key: 'discipline', label: 'Discipline', short: 'DISC' },
  { key: 'stamina', label: 'Stamina', short: 'STAM' },
  { key: 'smile', label: 'Smile', short: 'SMILE' },
  { key: 'execution', label: 'Execution', short: 'EXEC' },
  { key: 'nakhra', label: 'Nakhra', short: 'NAKHRA' },
];

// The order the approved app design reads them in, which is not alphabetical
// and not the order the client lists them in conversation. Keep the two apart:
// this array drives display, STAT_KEYS drives storage and validation.
export const STAT_KEYS = COMPETITION_STATS.map((stat) => stat.key);

export const MAX_STAT = 10;

/**
 * A coach's number. Null means "not recorded", which is different from zero —
 * the same distinction the ceremony makes between absent and a score of nought.
 *
 * One decimal place: the design shows 8.6 and 9.2, finer than the ceremony's
 * half-points, because a coach is separating dancers rather than passing them.
 */
export function isValidStat(value) {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (value < 0 || value > MAX_STAT) return false;
  return Math.abs(value * 10 - Math.round(value * 10)) < 1e-9;
}

/** Parses what a coach types — "8", "8.6", "" — and nothing else. */
export function parseStat(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return { value: null };
  if (!/^\d{0,2}(\.\d?)?$/.test(text)) return { invalid: true };
  const value = Number(text);
  if (!isValidStat(value)) return { invalid: true };
  return { value };
}

const round1 = (value) => Math.round(value * 10) / 10;

/**
 * Average each stat across a set of recorded sessions, then average those into
 * one score.
 *
 * Averaging the sessions rather than taking the latest one is deliberate: a
 * leaderboard that moves on a single bad practice would make the coach reluctant
 * to record bad practices, which is exactly the data worth having.
 *
 * A stat nobody filled in is skipped rather than counted as zero, so a coach can
 * record stamina at a fitness session without dragging everything else down.
 *
 * @param {object[]} entries CompetitionStat documents (or plain objects)
 * @returns {{stats: object, score: number|null, scoreExact: number|null,
 *   sessions: number, focusStat: string|null}}
 */
export function averageStats(entries = []) {
  const stats = {};
  const averages = [];

  for (const key of STAT_KEYS) {
    const values = entries
      .map((entry) => entry?.[key])
      .filter((value) => typeof value === 'number' && Number.isFinite(value));

    if (values.length === 0) {
      stats[key] = null;
      continue;
    }

    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    stats[key] = round1(mean);
    averages.push(mean);
  }

  if (averages.length === 0) {
    return { stats, score: null, scoreExact: null, sessions: entries.length, focusStat: null };
  }

  // The overall score is the mean of the stat averages, not the mean of every
  // number recorded — otherwise a stat the coach happens to fill in more often
  // quietly counts for more.
  const scoreExact = averages.reduce((sum, value) => sum + value, 0) / averages.length;

  return {
    stats,
    score: round1(scoreExact),
    scoreExact,
    sessions: entries.length,
    focusStat: lowestStat(stats),
  };
}

/**
 * The stat to work on — the lowest average, which is what the app highlights in
 * a different colour. Null when every recorded stat is level, because
 * arbitrarily picking one would tell a dancer to fix something that isn't
 * behind.
 */
function lowestStat(stats) {
  const scored = STAT_KEYS.filter((key) => stats[key] !== null);
  if (scored.length < 2) return null;

  const lowest = Math.min(...scored.map((key) => stats[key]));
  const highest = Math.max(...scored.map((key) => stats[key]));
  if (lowest === highest) return null;

  return scored.find((key) => stats[key] === lowest);
}

/**
 * Rank a team.
 *
 * Every member appears, including those with nothing recorded yet — the app
 * shows "#3 of 14", and a denominator that quietly excluded unscored dancers
 * would make a team look smaller than it is. Unscored members sort last and
 * carry a null position rather than a made-up one.
 *
 * Ties share a position and consume the ones behind them (1, 2, 2, 4), which is
 * how a competition places dancers.
 *
 * @param {object[]} members [{ studentKey, ... }] — the roster, in any order
 * @param {Map<string, object[]>} entriesByKey studentKey -> their stat documents
 */
export function leaderboard(members = [], entriesByKey = new Map()) {
  const rows = members.map((member) => ({
    ...member,
    ...averageStats(entriesByKey.get(member.studentKey) || []),
  }));

  rows.sort((a, b) => {
    if (a.scoreExact === null && b.scoreExact === null) return compareNames(a, b);
    if (a.scoreExact === null) return 1;
    if (b.scoreExact === null) return -1;
    if (b.scoreExact !== a.scoreExact) return b.scoreExact - a.scoreExact;
    return compareNames(a, b);
  });

  let position = 0;
  let previousScore = null;
  let sharedPosition = 0;

  return rows.map((row, index) => {
    if (row.scoreExact === null) return { ...row, position: null, teamSize: rows.length };

    position = index + 1;
    // Same score as the dancer above: they share that placing.
    if (previousScore !== null && Math.abs(row.scoreExact - previousScore) < 1e-9) {
      position = sharedPosition;
    } else {
      sharedPosition = position;
    }
    previousScore = row.scoreExact;

    return { ...row, position, teamSize: rows.length };
  });
}

const compareNames = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
